import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface FormInputProps {
  label: string
  name: string
  type?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  error?: string
  touched?: boolean
  placeholder?: string
  required?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  helpText?: string
  autoComplete?: string
  validation?: (value: string) => string | undefined
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  touched,
  placeholder,
  required = false,
  disabled = false,
  icon,
  helpText,
  autoComplete,
  validation
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const hasError = touched && error

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (onBlur) onBlur(e)
    if (validation) {
      validation(e.target.value)
      // This would need to be handled by the parent component
    }
  }

  return (
    <div className="w-full">
      <label 
        htmlFor={name}
        className={`block text-sm font-medium mb-1 transition-colors duration-200 ${
          hasError ? 'text-red-600' : isFocused ? 'text-blue-600' : 'text-gray-700'
        }`}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className={`transition-colors duration-200 ${
              hasError ? 'text-red-500' : isFocused ? 'text-blue-500' : 'text-gray-400'
            }`}>
              {icon}
            </div>
          </div>
        )}

        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`
            w-full px-3 py-2 border rounded-lg transition-all duration-200
            ${icon ? 'pl-10' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            ${hasError 
              ? 'border-red-500 focus:ring-2 focus:ring-red-200 focus:border-red-500' 
              : isFocused
                ? 'border-blue-500 focus:ring-2 focus:ring-blue-200'
                : 'border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-200 focus:border-blue-500'
            }
            placeholder-gray-400
          `}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
        />
      </div>

      {helpText && !hasError && (
        <p id={`${name}-help`} className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}

      <AnimatePresence>
        {hasError && (
          <motion.p
            id={`${name}-error`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-1 text-sm text-red-600 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FormInput